import React, { useEffect, useMemo, useState, useRef } from 'react'
import { debounce } from '@material-ui/core';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import ColorPicker from '../ColorPicker';
import ImageUploader from '../blocks/editable/ImageUploader';
import DeleteModal from './settings/DeleteModal'
import Toggle from '../sectioninputs/Toggle';
import Dropdown from '../Dropdown';
import { getDeviceType, createLayoutObject, getCleanedPixelValue, storefrontPopupPages, storefrontPopupDeviceTypesCollection, getStorefrontPopupApplicableStyles, getStorefrontPopupBlockStyleValue, storefrontPopupDeviceTypes, refreshIcon, generateUniqueIdForPopup, storefrontPopupClickActionCollection, storefrontPopupAlignCollection, storefrontPopupFontStyleCollection, storefrontPopupFieldNameCollection, storefrontPopupLinkTargetCollection, storefrontPopupBasicBlockElementList, subAndSaveTrigger, subAndSaveEventName } from '../constants/storefrontPopupConstants';
import { StorefrontPopupPositionSettings } from '../constants/storefrontPopupData';
import { storeFrontPopupData } from '../constants/storefrontPopupData';
import { storefrontPopupComponentMap, StorefrontPopupCloseButton } from '../constants/storefrontPopupBlocksComponent';
import Backend from '../../utils/Backend';
import General from '../../utils/General';
import FontSelect from '../FontSelect';
import { STANDARD_WEIGHTS } from '../../utils/Fonts';
import Currency from '../../utils/Currency';
import Notify from '../../utils/Notify';
import Cart from '../../utils/Cart';
import { findMatchingVariant, ssTags } from '../constants/utils';

const getStructuredClone = (data) => {
    return JSON.parse(JSON.stringify(data))
}

export const StorefrontPopupFontFamilySelect = (props) => {
    const { currentDevice, dataObj, handlePopupLayoutStyleChanges, showFontWeightSelect, parentClassName, popupData, labelClassName } = props
    const deviceStyleKey = `${currentDevice}Styles`
    let fontFamily = getStorefrontPopupBlockStyleValue(dataObj, deviceStyleKey, "fontFamily")
    fontFamily = fontFamily === "inherit" && popupData ? getStorefrontPopupBlockStyleValue(popupData, deviceStyleKey, "fontFamily") : fontFamily
    const fontWeight = showFontWeightSelect && getStorefrontPopupBlockStyleValue(dataObj, deviceStyleKey, "fontWeight")
    const font = FontSelect.getOption(fontFamily)
    let weights, selectedWeight;
    if (showFontWeightSelect) {
        weights = (font?.weights || STANDARD_WEIGHTS).map(weight => {
            return {
                label: weight.name,
                value: weight.value,
                className: font?.className ? `${font.className} ${font.className}-${weight.value}` : ""
            }
        })
        selectedWeight = weights.find(weight => weight.value === fontWeight)
    }
    return (
        <>
            <div className={parentClassName}>
                <div className={labelClassName}>
                    Font Family
                </div>
                <FontSelect
                    value={fontFamily}
                    onUpdated={value => {
                        handlePopupLayoutStyleChanges("fontFamily", value)
                    }}
                />
            </div>
            {showFontWeightSelect && <div className={parentClassName}>
                <div className={labelClassName}>
                    Font Weight
                </div>
                <Dropdown
                    options={weights}
                    value={selectedWeight}
                    onChange={option => {
                      handlePopupLayoutStyleChanges("fontWeight", option.value)
                    }}
                    placeholder=""
                    className="font-select"
                    placeholderClassName={font?.className ? `${font.className} ${font.className}-${fontWeight}` : null}
                />
            </div>}
        </>
    )
}

const RangeSlider = ({
    min = 0,
    max = 100,
    step = 1,
    value,
    onChange,
    label = "Slider",
    propertyName = "slider",
    nonPixelProperty,
    labelClassName,
    currentDevice
}) => {
    const [localState, setLocalState] = useState(value)
    useEffect(() => {
        setLocalState(value)
    }, [value, currentDevice])
    const percentage = ((localState - min) / (max - min)) * 100;
    const debouncedChangeCommitted = useMemo(() => {
        return debounce(onChange, 200);
    }, [onChange]);
    return (
        <>
            <div className='my-4'>
                <div className='d-flex flex-row justify-content-between'>
                    <div className={labelClassName}>
                        {label}
                    </div>
                    <div className='range-slider-value-container'>
                        {localState}{nonPixelProperty ? "" : "px"}
                    </div>
                </div>
                <div class="range-slider-container">
                    <input
                        step={step}
                        type="range"
                        min={min}
                        max={max}
                        value={localState}
                        class="range-slider"
                        style={{ background: `linear-gradient(to right, #1E85FF 0%, #1E85FF ${percentage}%, #e5e7eb ${percentage}%, #e5e7eb 100%)` }}
                        onChange={(e) => {
                            const value = e.target.value
                            setLocalState(value)
                            debouncedChangeCommitted(propertyName, value)
                        }}
                    />
                </div>
            </div>
        </>
    );
};

export const StorefrontPopupContainer = (props) => {
    const { editable, popupData, currentDevice, previewMode, handleClosePopup, editMode, campaignId, subAndSaveData } = props
    const { layoutType, layout, layoutPadding } = popupData
    const [formData, setFormData] = useState({})
    const [formDataError, setFormDataError] = useState({})
    const stickyPopups = ["topBar", "bottomBar", "leftSticky", "rightSticky"].includes(popupData.position)
    const [disableState, setDisableState] = useState(false)

    const closePopupHandler = () => {
        !disableState && handleClosePopup && handleClosePopup()
    }

    const handleProductSubscibe = async () => {
        if (popupData.trigger !== subAndSaveTrigger) {
            return
        }
        const frequencySelects = popupData?.layout?.filter((item) => item.key === "frequency" && item?.deviceType?.[currentDevice])
        const element = frequencySelects?.find((item) => formData?.[item.id]?.value)
        try {
            const { customMessage, product, subscribeAndSaveProduct, quantity, variant, frequency } = subAndSaveData
            const frequencyValue = formData?.[element?.id]?.value || (subscribeAndSaveProduct?.cycle1_billDelay ? `${subscribeAndSaveProduct.cycle1_billDelay} Days` : "")
            const subVariant = findMatchingVariant(subscribeAndSaveProduct, variant)
            await Cart.remove(product, variant, customMessage, quantity, frequency)
            const newCart = await Cart.add(subscribeAndSaveProduct, variant, customMessage, quantity, frequencyValue)
            const newSig = ssTags.key(subscribeAndSaveProduct, subVariant?.variantDetailId, frequencyValue)
            const sourcePid = product?.campaignProductId || null
            const originalVariantId = variant?.variantDetailId || null
            ssTags.set(newSig, { sourcePid, originalVariantId })
            if (props.updateCart) props.updateCart(newCart)
            closePopupHandler()
        } catch (e) {
            Notify.error(e?.message || "Could not switch to subscription.")
        }
    }

    const handleSubmitLead = (submitButtonElementId) => {
        if (disableState) {
            return
        }
        let data = {}
        const inputs = popupData?.layout?.filter((item) => item.key === "input" && item?.deviceType?.[currentDevice])
        if (inputs?.length) {
            const requiredInputs = inputs.filter((item) => item.required)
            const requiredInputsWithNoValue = requiredInputs?.length ? requiredInputs.filter((item) => ((formData?.[item.id]?.value || "").trim() ? false : true)) : []
            const anyInputHasValue = inputs.some((item) => ((formData?.[item.id]?.value || "").trim() ? true : false))
            if (anyInputHasValue) {
                if (!requiredInputsWithNoValue?.length) {
                    setFormDataError(data)
                    const params = {}
                    for (let i in formData) {
                        const item = formData[i]
                        params[item.name] = item.value
                    }
                    setDisableState(true)
                    const sessionId = sessionStorage.getItem("ccExternalSessionId")
                    if (sessionId) {
                        params[sessionId] = sessionId
                    }
                    params.salesUrl = window?.location?.href || ""
                    params.userAgent = window?.navigator?.userAgent || ""
                    params.campaignId = campaignId || ""
                    Backend.submitLead(params).then((response) => {
                        setDisableState(false)
                        if (response?.result?.toUpperCase() === "SUCCESS") {
                            closePopupHandler()
                        } else {
                            data[submitButtonElementId] = response?.message || "An error occured, please try again."
                            setFormDataError(data)
                        }
                    }).catch((err) => {
                        setDisableState(false)
                        data[submitButtonElementId] = err?.message || "An error occured, please try again."
                        setFormDataError(data)
                    })
                } else {
                    requiredInputsWithNoValue.forEach((item) => {
                        data[item.id] = `${item.name} is required`
                    })
                    setFormDataError(data)
                }
            } else {
                data[submitButtonElementId] = "Please fill form inputs"
                setFormDataError(data)
            }
        } else {
            setFormDataError(data)
        }
    }

    const getStyleSheet = () => {
        return getStorefrontPopupApplicableStyles(currentDevice, popupData, true)
    }

    const renderPopupContent = () => {
        let data = [layout];
        if (layoutType === "2") {
            data = [[], []];
            layout.forEach((item) => {
                item.position === "left" ? data[0].push(item) : data[1].push(item)
            })
        }
        return (
            <>
                {
                    data.map((array, index) => {
                        return <div key={index} className={`col-${12 / Number(layoutType)}`} style={{ padding: (layoutType === "1" ? layoutPadding["column"] : layoutPadding["column" + (index + 1)]) }}>
                            {
                                array.map((item, i) => {
                                    const { key, ...blockData } = item
                                    const ContentComponent = storefrontPopupComponentMap[key]
                                    if (!ContentComponent) {
                                        return
                                    }
                                    let dyanamicProps = {}
                                    if (key === "button" && !editMode) {
                                        dyanamicProps = {
                                            handleClosePopup: closePopupHandler,
                                            handleSubmitLead: handleSubmitLead,
                                            formDataError: formDataError,
                                            handleProductSubscibe: handleProductSubscibe
                                        }
                                    } else if (key === "input") {
                                        dyanamicProps = {
                                            formData: formData,
                                            setFormData: setFormData,
                                            formDataError: formDataError,
                                            setFormDataError: setFormDataError
                                        }
                                    } else if (key === "frequency" && popupData.trigger === subAndSaveTrigger) {
                                        dyanamicProps = {
                                            formData: formData,
                                            setFormData: setFormData,
                                            formDataError: formDataError,
                                            setFormDataError: setFormDataError,
                                            subProduct: subAndSaveData?.subscribeAndSaveProduct,
                                            product: subAndSaveData?.product
                                        }
                                    } else if (key === "text" && popupData.trigger === subAndSaveTrigger && subAndSaveData) {
                                        const subProduct = subAndSaveData?.subscribeAndSaveProduct
                                        const product = subAndSaveData?.product
                                        const variant = subAndSaveData?.variant
                                        const variantTitle = subProduct?.variants?.length && variant && variant.title
                                        if (subProduct && product) {
                                            const productPrice = variant?.price > 0 ? Number(variant.price) : Number(product.cycle1_price || 0)
                                            const subAndSaveVariant = subProduct?.variants?.find((o) => o.variantDetailId === variant?.variantDetailId)
                                            const subAndSaveProductPrice = subAndSaveVariant?.price > 0 ? Number(subAndSaveVariant.price) : Number(subProduct.cycle1_price || 0)
                                            let savingPrice = Number(productPrice) - Number(subAndSaveProductPrice)
                                            savingPrice = savingPrice > 0 ? savingPrice : 0
                                            const savingPercent = ((savingPrice / (Number(productPrice) || 1)) * 100).toFixed()
                                            dyanamicProps = {
                                                tokenReplacementObject: [
                                                    { regex: /{\s?{\s?productName\s?}\s?}/g, replaceWith: `${subProduct?.productName}${variantTitle ? `- ${variantTitle}` : ""}` },
                                                    { regex: /{\s?{\s?cycle1_price\s?}\s?}/g, replaceWith: Currency.toFormat(subAndSaveProductPrice) },
                                                    { regex: /{\s?{\s?savings\s?}\s?}/g, replaceWith: Currency.toFormat(savingPrice) },
                                                    { regex: /{\s?{\s?savingsPercent\s?}\s?}/g, replaceWith: savingPercent + "%" }
                                                ]
                                            }
                                        }
                                    }
                                    return <ContentComponent key={i} {...dyanamicProps} editMode={editMode} editable={editable} previewMode={previewMode} currentDevice={currentDevice} {...blockData} />
                                })
                            }
                        </div>
                    })
                }
            </>
        )
    }

    return (
        <>
            {
                !stickyPopups && <style>
                    {`
                        body {
                            overflow: hidden;
                        }
                    `}
                </style>
            }
            {
                stickyPopups && !editMode ? 
                    <div className='storefront-popup-container' style={{ ...getStyleSheet(), position: "fixed" }}>
                        <div className='row storefront-popup-content-box'>
                            {renderPopupContent()}
                        </div>
                        {(popupData.showCloseButton || stickyPopups) && <StorefrontPopupCloseButton popupData={popupData} handleClosePopup={closePopupHandler} />}
                    </div>
                    : <div
                        className={`storefront-popup-backdrop ${!editMode ? "" : "storefront-popup-backdrop-edit"}`}
                        style={{ backgroundColor: stickyPopups ? "rgb(14 13 13 / 77%)" : popupData.backdropColor }}
                        onClick={(popupData.showCloseButton || editMode) ? null : closePopupHandler}
                    >
                        {popupData && popupData.targetDevice[currentDevice] && <div className='storefront-popup-container' style={getStyleSheet()}>
                            <div className='row storefront-popup-content-box'>
                                {renderPopupContent()}
                            </div>
                            {(popupData.showCloseButton || stickyPopups) && <StorefrontPopupCloseButton popupData={popupData} handleClosePopup={closePopupHandler} />}
                        </div>}
                    </div>
            }
        </>
    )
}


const EditCreateStorefrontPopups = (props) => {
    const { setPopupEditMode, onHide, setCurrentPopup, popupCollection, setDeletePopupId, setShowDeleteModal, pages, setPopupPreviewMode, previewMode } = props
    const [localPopupCollection, setLocalPopupCollection] = useState([])
    const [search, setSearch] = useState("")
    const [select, setSelect] = useState("all")
    const [spinRefreshTriggerIcon, setSpinRefreshTriggerIcon] = useState(false)

    useEffect(() => {
        const seachText = search.toLowerCase()
        const data = popupCollection.filter((item) => {
            return item?.name?.toLowerCase()?.includes(seachText) && (select === "all" || item.targetPage === select)
        })
        setLocalPopupCollection(data)
    }, [popupCollection, search, select])

    return (
        <>
            <div className='p-2'>
                <div className='d-flex justify-content-between align-items-center mb-5'>
                    <div className='storefront-popup-manager-title'>
                        Popup Manager
                    </div>
                    <div
                        className='storefront-popup-manager-close'
                        onClick={() => {
                            onHide()
                        }}
                    >
                        &times;
                    </div>
                </div>
                <div className='d-flex flex-row justify-content-end align-items-center'>
                    <div>
                        <button
                            className='storefront-add-new-popup-btn'
                            onClick={() => {
                                setPopupEditMode(true)
                                const data = getStructuredClone(storeFrontPopupData[0])
                                data.id = new Date().getTime()
                                data.name = `Popup ${data.id}`
                                setCurrentPopup(data)
                            }}
                        >
                            <span
                                className='fa-plus fas'
                            ></span> Add New Popup
                        </button>
                    </div>
                </div>
                <div>
                    <div className='storefront-popup-preview-mode-toggle'>
                        <Toggle
                            id="toggle_popup_preview_mode"
                            checked={previewMode}
                            title="Preview Mode"
                            onChange={checked => {
                                setPopupPreviewMode(checked)
                            }}
                        />
                    </div>
                </div>
                <div className='storefront-popup-refresh-trigger d-flex flex-row justify-content-between'>
                    <div>
                        <h4>Refresh Triggers</h4>
                    </div>
                    <div
                        className={spinRefreshTriggerIcon ? "storefront-popup-refresh-trigger-spin" : ""}
                        onClick={() => {
                            setSpinRefreshTriggerIcon(true)
                            setTimeout(() => {
                                sessionStorage.removeItem("ccStorefrontPopHasShown")
                                setSpinRefreshTriggerIcon(false)
                            }, 2000)
                        }}>
                        {refreshIcon}
                    </div>
                </div>
                <div className='d-flex flex-row m-2'>
                    <div className='storefront-popup-page-search-container'>
                        <input
                            type="text"
                            placeholder='Search'
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                    <div className='storefront-popup-page-select-container'>
                        <select
                            value={select || "all"}
                            onChange={(event) => {
                                setSelect(event.target.value)
                            }}
                        >
                            <option key={0} value="all">All Pages</option>
                            {
                                pages?.map((item, index) => {
                                    return <option key={index + 1} value={item.slugConstant}>{item.pageName}</option>
                                })
                            }
                        </select>
                    </div>
                </div>
                <div className='p-2'>
                    <div className='storefront-popup-row storefront-popup-row-head'>
                        <div className='storefront-popup-row-name'>Name</div>
                        <div className='storefront-popup-row-action text-center'>Action</div>
                    </div>
                    <div className='storefront-popup-list-container'>
                        {
                            localPopupCollection?.map((item, index) => {
                                return (
                                    <div key={index} className='storefront-popup-row'>
                                        <div className='storefront-popup-row-name'>
                                            {item.name}
                                        </div>
                                        <div className='storefront-popup-row-action d-flex justify-content-center'>
                                            <div className='p-2'>
                                                <span
                                                    onClick={() => {
                                                        setPopupEditMode(true)
                                                        setCurrentPopup(getStructuredClone(item))
                                                    }}
                                                    className='fa-pen fas cursor-pointer'
                                                    title='Edit'
                                                ></span>
                                            </div>
                                            <div className='p-2'>
                                                <span
                                                    onClick={() => {
                                                        setPopupEditMode(true)
                                                        const data = getStructuredClone(item)
                                                        data.id = generateUniqueIdForPopup()
                                                        setCurrentPopup(data)
                                                    }}
                                                    className='fa-clone fas cursor-pointer'
                                                    title='Clone'
                                                ></span>
                                            </div>
                                            <div className='p-2'>
                                                <span
                                                    onClick={() => {
                                                        setDeletePopupId(item.id)
                                                        setShowDeleteModal(true)
                                                    }}
                                                    className='fa-trash fas cursor-pointer'
                                                    title='Delete'
                                                ></span>
                                            </div>
                                        </div>
                                    </div>
                                )
                            })
                        }
                    </div>
                </div>
            </div>
        </>
    )
}

const StorefrontPopupBlockActionButton = (props) => {
    const { block, setEditElement, removeElement } = props
    const [show, setShow] = useState(false)
    return (
        <>
            <div className='storefront-popup-position-relative' onBlur={() => { setShow(false) }}>
                <button
                    className='storefront-popup-more-action-btn'
                    onClick={() => { setShow(!show) }}
                    onBlur={() => { setShow(false) }}
                >
                    <span className='fa-ellipsis-v fas'></span>
                </button>
                {show && <div
                    className='dropdown-menu storefront-popup-action-dropdown'
                >
                    <div
                        className='storefront-popup-block-edit-option d-flex flex-row justify-content-between align-items-center cursor-pointer p-2'
                        onClick={() => {
                            setShow(false)
                            const editBlock = getStructuredClone(block)
                            setEditElement(editBlock)
                        }}
                    >
                        <div>Edit</div>
                        <div className='fa-pen fas'></div>
                    </div>
                    <div
                        className='storefront-popup-block-edit-option d-flex flex-row justify-content-between align-items-center cursor-pointer p-2'
                        onClick={() => {
                            setShow(false)
                            removeElement(block.id)
                        }}
                    >
                        <div>Delete</div>
                        <div className='fa-trash fas'></div>
                    </div>
                </div>}
            </div>
        </>
    )
}

const StorefrontPopupLayoutEditor = (props) => {
    const { popupData, handlePopupLayoutUpdate, defaultPopupData, setEditElement, removeElement, version, resetVersion, resetCurrentPopupData } = props
    const onReset = () => {
        resetCurrentPopupData()
        resetVersion()
    }
    const handleDragEnd = (result) => {
        const { source, destination } = result
        if (!destination) {
            return;
        }
        const updatedLayout = [...popupData.layout]
        const movingItemIndex = updatedLayout.findIndex((item, idx) => {
            const group = popupData.layout.filter(i => i.position === source.droppableId)
            return item === group[source.index]
        })

        if (movingItemIndex === -1) {
            return;
        }

        const [movedItem] = updatedLayout.splice(movingItemIndex, 1)
        movedItem.position = destination.droppableId;

        const reordered = [];
        let insertIndex = 0;

        for (let i = 0; i < updatedLayout.length; i++) {
            const current = updatedLayout[i];
            if (current.position === destination.droppableId) {
                if (insertIndex === destination.index) {
                    reordered.push(movedItem)
                }
                reordered.push(current)
                insertIndex++
            } else {
                reordered.push(current)
            }
        }

        if (!reordered.includes(movedItem)) {
            reordered.push(movedItem)
        }

        handlePopupLayoutUpdate(reordered, popupData.layoutType)
    }

    const renderDraggable = (block, index) => {
        return (
            <Draggable key={block.id} draggableId={block.id} index={index}>
                {(provided, snapshot) => (
                    <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        style={{
                            ...provided.draggableProps.style,
                            border: "1px solid #ccc",
                            padding: "8px",
                            background: snapshot.isDragging ? "#e0f7fa" : "#fff",
                            borderRadius: "4px",
                            fontSize: "14px",
                            textAlign: "center",
                            boxShadow: snapshot.isDragging ? "0 2px 8px rgba(0,0,0,0.15)" : "none"
                        }}
                    >
                        <div className='d-flex flex-row justify-content-between align-items-center'>
                            <div className='d-flex flex-row justify-content-start align-items-center storefront-popup-block-item-content-container'>
                                <div className='storefront-popup-block-item-icon'>
                                    {block.key.charAt(0).toUpperCase()}
                                </div>
                                <div className='storefront-popup-block-text'>
                                    <div className='storefront-popup-block-key'>
                                        {block.key}
                                    </div>
                                    <div className='storefront-popup-block-label'>
                                        {block.label}
                                    </div>
                                </div>
                            </div>
                            <StorefrontPopupBlockActionButton removeElement={removeElement} block={block} setEditElement={setEditElement} index={index} />
                        </div>
                    </div>
                )}
            </Draggable>
        );
    };

    const getBlocks = (layout, position) => {
        return [...layout].filter(b => b.position === position)
    }

    const renderDroppable = (position, fullWidth = false) => {
        const blocks = getBlocks(popupData.layout, position);
        const containerClass = fullWidth ? "col-12" : "col-md-6";

        return (
            <div className={`${containerClass} px-2`}>
                <Droppable droppableId={position}>
                    {(provided, snapshot) => (
                        <div
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            style={{
                                backgroundColor: snapshot.isDraggingOver ? "#e3f2fd" : "#f8f9fa",
                                border: snapshot.isDraggingOver ? "2px dashed #2196f3" : "1px solid #dee2e6",
                                borderRadius: "6px",
                                padding: "5px",
                                marginTop: "6px",
                                display: "flex",
                                flexDirection: "column",
                                gap: "5px",
                            }}
                        >
                            {blocks.map((block, index) => renderDraggable(block, index))}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </div>
        )
    }
    return (
        <>
            <div className='storefront-popup-position-relative'>
                <div className='p-2 my-2'>
                    <div className='storefront-popup-settings-sub-heading'>
                        Column
                    </div>
                    <div>
                        <select
                            className='w-100 mt-2'
                            value={popupData.layoutType || "1"}
                            onChange={
                                (event) => {
                                    const value = event.target.value
                                    const position = value === "1" ? "row" : "left";
                                    const array = [...popupData.layout];
                                    array.forEach((item) => {
                                        item.position = position;
                                    });
                                    handlePopupLayoutUpdate(array, value);
                                }
                            }
                        >
                            <option value="1">1 Column</option>
                            <option value="2">2 Column</option>
                        </select>
                    </div>
                </div>
                <div className='p-2 d-flex flex-row justify-content-between my-2'>
                    <div className='storefront-popup-settings-sub-heading'>
                        Blocks
                    </div>
                    <div>
                        {defaultPopupData.layout && <span className='cursor-pointer storefront-popup-settings-reset-color' onClick={onReset}>
                            <span className="fa-undo fas">
                            </span>
                            <span className='storefront-popup-settings-reset-text'> Reset</span>
                        </span>}
                    </div>
                </div>
                <div className='droppable-zone'>
                    <DragDropContext onDragEnd={handleDragEnd} key={version}>
                        {
                            popupData?.layoutType === "2" ?
                                <div className="row m-0 p-0">
                                    {renderDroppable("left")}
                                    {renderDroppable("right")}
                                </div>
                                :
                                <div className="row m-0 p-0">
                                    {renderDroppable("row", true)}
                                </div>
                        }
                    </DragDropContext>
                </div>
            </div>
        </>
    )
}

const StorefrontEditBuilderLayer = (props) => {
    const { popupData, handlePopupLayoutUpdate, setAddNewBlock, defaultPopupData, setEditElement, removeElement, resetCurrentPopupData } = props
    const [version, setVersion] = useState(1);
    const resetVersion = () => {
        setVersion((v) => v + 1)
    }
    return (
        <>
            <div className='p-2'>
                <div className='storefront-popup-settings-heading'>
                    Builder
                </div>
                <div>
                    <StorefrontPopupLayoutEditor resetCurrentPopupData={resetCurrentPopupData} version={version} resetVersion={resetVersion} removeElement={removeElement} setEditElement={setEditElement} defaultPopupData={defaultPopupData} popupData={popupData} handlePopupLayoutUpdate={handlePopupLayoutUpdate} />
                </div>
                <div className='p-2'>
                    <button onClick={() => { setAddNewBlock(true) }} className='storefront-popup-add-element-btn'>
                        <span className='fa-plus fas'></span>
                        <span> Add Element</span>
                    </button>
                </div>
            </div>
        </>
    )
}

const StorefrontPopupBuilderBlocks = (props) => {
    const { show, setShow, setNewElement, layoutType, subAndSavePopup } = props
    const blockList = subAndSavePopup ? storefrontPopupBasicBlockElementList : storefrontPopupBasicBlockElementList.filter((o) => o !== "frequency");
    return (
        <>
            {
                show && <div className='storefront-popup-position-relative'>
                    <div
                        className='storefront-popup-builder-block-container'
                    >
                        <div>
                            <div className='d-flex flex-row justify-content-between align-items-end'>
                                <div className='storefront-popup-builder-block-dead-block'>
                                </div>
                                <div className='storefront-popup-settings-heading'>
                                    Add Element
                                </div>
                                <div
                                    className='storefront-popup-settings-heading-action'
                                    onClick={() => { setShow(false) }}
                                >
                                    Close
                                </div>
                            </div>
                            <div className='d-flex flex-row flex-wrap mt-4'>
                                {
                                    blockList.map((item) => {
                                        return (
                                            <div
                                                className='m-2'
                                                onClick={() => {
                                                    let data = createLayoutObject(item, { label: `New ${item}`, position: layoutType === "2" ? "left" : "row" })
                                                    data.id = generateUniqueIdForPopup()
                                                    setNewElement(getStructuredClone(data))
                                                }}
                                            >
                                                <div className='storefront-popup-add-element-icon'>
                                                    <span>{item.charAt(0).toUpperCase()}</span>
                                                </div>
                                                <div className='storefront-popup-add-element-icon-text'>
                                                    {item}
                                                </div>
                                            </div>
                                        )
                                    })
                                }
                            </div>
                        </div>
                    </div>
                </div>
            }
        </>
    )
}

const StorefrontPopupElementSettingHeader = ({ value }) => {
    return (
        <div className='my-3 storefront-popup-settings-section-heading'>
            {value}
        </div>
    )
}

const StorefrontPopupUpdateColorComponent = (props) => {
    const { label, deviceStyleKey, elementData, updateCallback, propertyKey, containerClassName = 'my-4', labelClassName = 'storefront-popup-field-label' } = props
    return (
        <div className={containerClassName}>
            <div className={labelClassName}>
                {label}
            </div>
            <div className='storefront-popup-color-selector'>
                <ColorPicker
                    title={""}
                    color={getStorefrontPopupBlockStyleValue(elementData, deviceStyleKey, propertyKey)}
                    onColorSelected={color => {
                        updateCallback(propertyKey, color)
                    }}
                />
            </div>
        </div>
    )
}

const StorefrontPopupUpdateTextComponent = (props) => {
    const { label, updateCallback, elementData, value, valueKey = 'label', labelClassName = 'storefront-popup-field-label' } = props
    return (
        <div className='my-4'>
            <div className={labelClassName}>
                {label}
            </div>
            <div>
                <input
                    type="text"
                    value={value}
                    onChange={(event) => {
                        const data = getStructuredClone(elementData)
                        data[valueKey] = event.target.value || ""
                        updateCallback(data)
                    }}
                />
            </div>
        </div>
    )
}

const StorefrontPopupUpdateDeviceOnComponent = (props) => {
    const { updateBlockElement, elementData, idPrefix = 'toggle_target_device_', deviceKey = 'deviceType', labelClassName = 'storefront-popup-field-label' } = props
    return (
        <div className='my-4'>
            <div className={labelClassName}>
                Display On
            </div>
            {
                storefrontPopupDeviceTypesCollection.map((item, index) => {
                    return <div key={index} className='storefront-popup-settings-toggle'>
                        <Toggle
                            id={`${idPrefix}${item.device}`}
                            checked={elementData[deviceKey][item.device] || false}
                            title={item.deviceName}
                            onChange={checked => {
                                const data = getStructuredClone(elementData)
                                data[deviceKey][item.device] = checked;
                                updateBlockElement(data);
                            }}
                        />
                    </div>
                })
            }
        </div>
    )
}

const StorefrontPopupUpdateSelectComponent = (props) => {
    const { label, labelClassName = 'storefront-popup-field-label', value, updateCallback, dataKey, optionData, elementData } = props
    return (
        <div className='my-4'>
            <div className={labelClassName}>
                {label}
            </div>
            <div>
                <select
                    className='w-100 mt-2'
                    value={value}
                    onChange={(event) => {
                        const data = getStructuredClone(elementData)
                        data[dataKey] = event.target.value
                        updateCallback(data)
                    }}
                >
                    {optionData.map(([value, label], index) => {
                        return <option index={index} value={value}>{label}</option>
                    })}
                </select>
            </div>
        </div>
    )
}

const StorefrontPopupUpdateStyleSelectComponent = (props) => {
    const { label, labelClassName = 'storefront-popup-field-label', updateCallback, optionData, elementData, deviceStyleKey, property } = props
    return (
        <div className='my-4'>
            <div className={labelClassName}>
                {label}
            </div>
            <div>
                <select
                    className='w-100 mt-2'
                    value={getStorefrontPopupBlockStyleValue(elementData, deviceStyleKey, property)}
                    onChange={(event) => {
                        updateCallback(property, event.target.value)
                    }}
                >
                    {optionData.map(([value, label], index) => {
                        return <option index={index} value={value}>{label}</option>
                    })}
                </select>
            </div>
        </div>
    )
}

const StorefrontPopupUpdateHeightWidthStyleComponent = (props) => {
    const { label, value, propertyKey, defaultPixelValue, updateCallback, min, max, showFullHeight } = props
    return (
        <div className='my-4'>
            <div className='storefront-popup-field-label'>
                {label}
            </div>
            <div>
                <select
                    className='w-100 mt-2'
                    value={["auto", "100%"].includes(value) ? value : "px"}
                    onChange={(event) => {
                        if (event.target.value !== "px") {
                            updateCallback(propertyKey, event.target.value)
                        } else {
                            updateCallback(propertyKey, defaultPixelValue)
                        }
                    }}
                >
                    <option value="auto">Auto</option>
                    {showFullHeight && <option value="100%">Full {label}</option>}
                    <option value="px">PX</option>
                </select>
            </div>
            {!["auto", "100%"].includes(value) && <RangeSlider
                label=""
                min={min}
                max={max}
                step={1}
                value={getCleanedPixelValue(value)}
                onChange={(property, value) => {
                    updateCallback(property, `${value}px`)
                }}
                propertyName={propertyKey}
                labelClassName="storefront-popup-block-sub-heading"
            />}
        </div>
    )
}

const StorefrontPopupTextBlockSettings = (props) => {
    const { elementData, updateBlockElement, currentDevice, popupData } = props
    const deviceStyleKey = `${currentDevice}Styles`;

    const handlePopupLayoutStyleChanges = (property, value) => {
        const data = getStructuredClone(elementData)
        if (["fontSize"].includes(property)) {
            data[deviceStyleKey][property] = `${value}px`
        } else {
            data[deviceStyleKey][property] = value
        }
        updateBlockElement(data)
    }
    
    return (
        <>
            <div className='mb-3'>
                <StorefrontPopupElementSettingHeader value='Settings' /> 
                <StorefrontPopupUpdateTextComponent label="Text" updateCallback={updateBlockElement} elementData={elementData} value={elementData.label} />
                <StorefrontPopupUpdateSelectComponent label='Align' value={elementData.align || "left"} updateCallback={updateBlockElement} dataKey='align' elementData={elementData} optionData={storefrontPopupAlignCollection} />
                <StorefrontPopupUpdateDeviceOnComponent elementData={elementData} updateBlockElement={updateBlockElement}  />
            </div>
            <div>
                <StorefrontPopupElementSettingHeader value={`${storefrontPopupDeviceTypes[currentDevice]} Appearence`} />
                <StorefrontPopupUpdateColorComponent elementData={elementData} deviceStyleKey={deviceStyleKey} label='Font Color' propertyKey='color' updateCallback={handlePopupLayoutStyleChanges} />
                <div className='my-4'>
                    <RangeSlider
                        rid={elementData.id}
                        label="Font Size"
                        min={10}
                        max={100}
                        step={1}
                        value={getCleanedPixelValue(getStorefrontPopupBlockStyleValue(elementData, deviceStyleKey, "fontSize"))}
                        onChange={handlePopupLayoutStyleChanges}
                        propertyName="fontSize"
                        labelClassName="storefront-popup-block-sub-heading"
                    />
                </div>
                <StorefrontPopupUpdateStyleSelectComponent label='Font Style' deviceStyleKey={deviceStyleKey} property='fontStyle' updateCallback={handlePopupLayoutStyleChanges} elementData={elementData} optionData={storefrontPopupFontStyleCollection} />
                <StorefrontPopupFontFamilySelect labelClassName="storefront-popup-block-sub-heading" popupData={popupData} showFontWeightSelect={true} dataObj={elementData} currentDevice={currentDevice} handlePopupLayoutStyleChanges={handlePopupLayoutStyleChanges} parentClassName="my-4" />
            </div>
        </>
    )
}

const StorefrontPopupImageBlockSettings = (props) => {
    const { elementData, updateBlockElement, currentDevice } = props
    const deviceStyleKey = `${currentDevice}Styles`;
    const handlePopupLayoutStyleChanges = (property, value) => {
        const data = getStructuredClone(elementData)
        if (["fontSize"].includes(property)) {
            data[deviceStyleKey][property] = `${value}px`
        } else {
            data[deviceStyleKey][property] = value
        }
        updateBlockElement(data)
    }
    const widthStyle = getStorefrontPopupBlockStyleValue(elementData, deviceStyleKey, "width")
    const heightStyle = getStorefrontPopupBlockStyleValue(elementData, deviceStyleKey, "height")
    return (
        <>
            <div>
                <StorefrontPopupElementSettingHeader value='Settings' />
                <StorefrontPopupUpdateTextComponent label="Alt Text" updateCallback={updateBlockElement} elementData={elementData} value={elementData.label} />
                <div className='my-4'>
                    <div className='storefront-popup-field-label'>
                        Source
                    </div>
                    <div className='my-3'>
                        <ImageUploader
                            title={false}
                            src={elementData.src || ""}
                            closeOnUploaded={false}
                            onUploaded={(image) => {
                                const data = getStructuredClone(elementData)
                                data.src = image.original || ""
                                updateBlockElement(data)
                            }}
                        />
                    </div>
                </div>
                <StorefrontPopupUpdateDeviceOnComponent elementData={elementData} updateBlockElement={updateBlockElement}  />
            </div>
            <div>
                <StorefrontPopupElementSettingHeader value={`${storefrontPopupDeviceTypes[currentDevice]} Appearence`} />
                <StorefrontPopupUpdateHeightWidthStyleComponent label='Width' value={widthStyle} defaultPixelValue='200px' propertyKey='width' min={10} max={1000} showFullHeight={true} updateCallback={handlePopupLayoutStyleChanges} />
                <StorefrontPopupUpdateHeightWidthStyleComponent label='Height' value={heightStyle} deviceStyleKey={deviceStyleKey} defaultPixelValue='200px' propertyKey='height' min={10} max={1000} showFullHeight={true} updateCallback={handlePopupLayoutStyleChanges} />
            </div>
        </>
    )
}

const StorefrontPopupButtonBlockSettings = (props) => {
    const { elementData, updateBlockElement, currentDevice, popupData } = props
    const deviceStyleKey = `${currentDevice}Styles`;
    const handlePopupLayoutStyleChanges = (property, value) => {
        const data = getStructuredClone(elementData)
        if (["fontSize", "borderRadius", "padding", "borderWidth"].includes(property)) {
            data[deviceStyleKey][property] = `${value}px`
        } else {
            data[deviceStyleKey][property] = value
        }
        updateBlockElement(data)
    }
    const widthStyle = getStorefrontPopupBlockStyleValue(elementData, deviceStyleKey, "width")
    const heightStyle = getStorefrontPopupBlockStyleValue(elementData, deviceStyleKey, "height")
    const clickActionCollection = popupData.trigger === subAndSaveTrigger ? storefrontPopupClickActionCollection : storefrontPopupClickActionCollection.filter((o) => o[0] !== "subscribe")
    return (
        <>
            <div>
                <StorefrontPopupElementSettingHeader value='Settings' />
                <StorefrontPopupUpdateTextComponent label="Button Text" updateCallback={updateBlockElement} elementData={elementData} value={elementData.label} />
                <StorefrontPopupUpdateSelectComponent label='Select Click Action' value={elementData.action || "none"} updateCallback={updateBlockElement} dataKey='action' elementData={elementData} optionData={clickActionCollection} />
                <StorefrontPopupUpdateSelectComponent label='Alignment' value={elementData.align || "center"} updateCallback={updateBlockElement} dataKey='align' elementData={elementData} optionData={storefrontPopupAlignCollection} />
                <StorefrontPopupUpdateDeviceOnComponent elementData={elementData} updateBlockElement={updateBlockElement}  />
            </div>
            <div>
                <StorefrontPopupElementSettingHeader value={`${storefrontPopupDeviceTypes[currentDevice]} Appearence`} />
                <StorefrontPopupUpdateColorComponent label='Text Color' propertyKey='color' elementData={elementData} deviceStyleKey={deviceStyleKey} updateCallback={handlePopupLayoutStyleChanges} />
                <StorefrontPopupUpdateColorComponent label='Background Color' propertyKey='backgroundColor' elementData={elementData} deviceStyleKey={deviceStyleKey} updateCallback={handlePopupLayoutStyleChanges} />
                <StorefrontPopupUpdateColorComponent label='Border Color' propertyKey='borderColor' elementData={elementData} deviceStyleKey={deviceStyleKey} updateCallback={handlePopupLayoutStyleChanges} />
                <div className='my-4'>
                    <RangeSlider
                        label="Border Width"
                        min={0}
                        max={50}
                        step={1}
                        value={getCleanedPixelValue(getStorefrontPopupBlockStyleValue(elementData, deviceStyleKey, "borderWidth"))}
                        onChange={handlePopupLayoutStyleChanges}
                        propertyName="borderWidth"
                        labelClassName="storefront-popup-block-sub-heading"
                    />
                </div>
                <div className='my-4'>
                    <RangeSlider
                        label="Font Size"
                        min={10}
                        max={50}
                        step={1}
                        value={getCleanedPixelValue(getStorefrontPopupBlockStyleValue(elementData, deviceStyleKey, "fontSize"))}
                        onChange={handlePopupLayoutStyleChanges}
                        propertyName="fontSize"
                        labelClassName="storefront-popup-block-sub-heading"
                    />
                </div>
                <StorefrontPopupUpdateStyleSelectComponent label='Font Style' deviceStyleKey={deviceStyleKey} property='fontStyle' updateCallback={handlePopupLayoutStyleChanges} elementData={elementData} optionData={storefrontPopupFontStyleCollection} />
                <StorefrontPopupFontFamilySelect labelClassName="storefront-popup-block-sub-heading" popupData={popupData} showFontWeightSelect={true} dataObj={elementData} currentDevice={currentDevice} handlePopupLayoutStyleChanges={handlePopupLayoutStyleChanges} parentClassName="my-4" />
                <div className='my-4'>
                    <RangeSlider
                        label="Corner Radius"
                        min={0}
                        max={100}
                        step={1}
                        value={getCleanedPixelValue(getStorefrontPopupBlockStyleValue(elementData, deviceStyleKey, "borderRadius"))}
                        onChange={handlePopupLayoutStyleChanges}
                        propertyName="borderRadius"
                        labelClassName="storefront-popup-block-sub-heading"
                    />
                </div>
                <div className='my-4'>
                    <RangeSlider
                        label="Padding"
                        min={0}
                        max={100}
                        step={1}
                        value={getCleanedPixelValue(getStorefrontPopupBlockStyleValue(elementData, deviceStyleKey, "padding"))}
                        onChange={handlePopupLayoutStyleChanges}
                        propertyName="padding"
                        labelClassName="storefront-popup-block-sub-heading"
                    />
                </div>
                <StorefrontPopupUpdateHeightWidthStyleComponent label='Width' value={widthStyle} defaultPixelValue='200px' propertyKey='width' min={10} max={500} showFullHeight={true} updateCallback={handlePopupLayoutStyleChanges} />
                <StorefrontPopupUpdateHeightWidthStyleComponent label='Height' value={heightStyle} deviceStyleKey={deviceStyleKey} defaultPixelValue='80px' propertyKey='height' min={10} max={500} showFullHeight={false} updateCallback={handlePopupLayoutStyleChanges} />
            </div>
        </>
    )
}

const StorefrontPopupSpacingBlockSettings = (props) => {
    const { elementData, updateBlockElement, currentDevice } = props
    const deviceStyleKey = `${currentDevice}Styles`
    const handlePopupLayoutStyleChanges = (property, value) => {
        const data = getStructuredClone(elementData)
        if (["height"].includes(property)) {
            data[deviceStyleKey][property] = `${value}px`
        } else {
            data[deviceStyleKey][property] = value
        }
        updateBlockElement(data)
    }
    return (
        <>
            <div>
                <StorefrontPopupElementSettingHeader value='Settings' />
                <StorefrontPopupUpdateTextComponent label="Label" updateCallback={updateBlockElement} elementData={elementData} value={elementData.label} />
                <StorefrontPopupUpdateDeviceOnComponent elementData={elementData} updateBlockElement={updateBlockElement}  />
                <StorefrontPopupElementSettingHeader value={`${storefrontPopupDeviceTypes[currentDevice]} Appearence`} />
                <div className='my-4'>
                    <RangeSlider
                        label="Spacing"
                        min={0}
                        max={100}
                        step={1}
                        value={getCleanedPixelValue(getStorefrontPopupBlockStyleValue(elementData, deviceStyleKey, "height"))}
                        onChange={handlePopupLayoutStyleChanges}
                        propertyName="height"
                        labelClassName="storefront-popup-block-sub-heading"
                    />
                </div>
            </div>
        </>
    )
}

const StorefrontPopupLinkBlockSettings = (props) => {
    const { elementData, updateBlockElement, currentDevice, popupData } = props
    const deviceStyleKey = `${currentDevice}Styles`
    const handlePopupLayoutStyleChanges = (property, value) => {
        const data = getStructuredClone(elementData)
        if (["fontSize", "padding"].includes(property)) {
            data[deviceStyleKey][property] = `${value}px`
        } else {
            data[deviceStyleKey][property] = value
        }
        updateBlockElement(data)
    }
    const widthStyle = getStorefrontPopupBlockStyleValue(elementData, deviceStyleKey, "width")
    return (
        <>
            <div>
                <StorefrontPopupElementSettingHeader value='Settings' />
                <StorefrontPopupUpdateTextComponent label="Link Text" updateCallback={updateBlockElement} elementData={elementData} value={elementData.label} />
                <StorefrontPopupUpdateTextComponent label="Link Href" updateCallback={updateBlockElement} elementData={elementData} value={elementData.href} valueKey="href" />
                <StorefrontPopupUpdateSelectComponent label='Target Type' value={elementData.target} updateCallback={updateBlockElement} dataKey='target' elementData={elementData} optionData={storefrontPopupLinkTargetCollection} />
                <StorefrontPopupUpdateSelectComponent label='Alignment' value={elementData.align} updateCallback={updateBlockElement} dataKey='align' elementData={elementData} optionData={storefrontPopupAlignCollection} />
                <StorefrontPopupUpdateDeviceOnComponent elementData={elementData} updateBlockElement={updateBlockElement}  />
            </div>
            <div>
                <StorefrontPopupElementSettingHeader value={`${storefrontPopupDeviceTypes[currentDevice]} Appearence`} />
                <StorefrontPopupUpdateColorComponent label='Color' propertyKey='color' elementData={elementData} deviceStyleKey={deviceStyleKey} updateCallback={handlePopupLayoutStyleChanges} />
                <div className='my-4'>
                    <RangeSlider
                        label="Font Size"
                        min={10}
                        max={50}
                        step={1}
                        value={getCleanedPixelValue(getStorefrontPopupBlockStyleValue(elementData, deviceStyleKey, "fontSize"))}
                        onChange={handlePopupLayoutStyleChanges}
                        propertyName="fontSize"
                        labelClassName="storefront-popup-block-sub-heading"
                    />
                </div>
                <StorefrontPopupUpdateStyleSelectComponent label='Font Style' deviceStyleKey={deviceStyleKey} property='fontStyle' updateCallback={handlePopupLayoutStyleChanges} elementData={elementData} optionData={storefrontPopupFontStyleCollection} />
                <StorefrontPopupFontFamilySelect labelClassName="storefront-popup-block-sub-heading" popupData={popupData} showFontWeightSelect={true} dataObj={elementData} currentDevice={currentDevice} handlePopupLayoutStyleChanges={handlePopupLayoutStyleChanges} parentClassName="my-4" />
                <div className='my-4'>
                    <RangeSlider
                        label="Padding"
                        min={0}
                        max={100}
                        step={1}
                        value={getCleanedPixelValue(getStorefrontPopupBlockStyleValue(elementData, deviceStyleKey, "padding"))}
                        onChange={handlePopupLayoutStyleChanges}
                        propertyName="padding"
                        labelClassName="storefront-popup-block-sub-heading"
                    />
                </div>
                <StorefrontPopupUpdateHeightWidthStyleComponent label='Width' value={widthStyle} defaultPixelValue='200px' propertyKey='width' min={10} max={500} showFullHeight={true} updateCallback={handlePopupLayoutStyleChanges} />
            </div>
        </>
    )
}

const StorefrontPopupInputBlockSettings = (props) => {
    const { elementData, updateBlockElement, currentDevice, popupData } = props
    const deviceStyleKey = `${currentDevice}Styles`
    const handlePopupLayoutStyleChanges = (property, value) => {
        const data = getStructuredClone(elementData)
        if (["fontSize", "borderRadius", "padding", "borderWidth"].includes(property)) {
            data[deviceStyleKey][property] = `${value}px`
        } else {
            data[deviceStyleKey][property] = value
        }
        updateBlockElement(data)
    }
    const widthStyle = getStorefrontPopupBlockStyleValue(elementData, deviceStyleKey, "width")
    const heightStyle = getStorefrontPopupBlockStyleValue(elementData, deviceStyleKey, "height")
    return (
        <>
            <div>
                <StorefrontPopupElementSettingHeader value='Settings' />
                <StorefrontPopupUpdateTextComponent label="Placeholder" updateCallback={updateBlockElement} elementData={elementData} value={elementData.label} />
                <StorefrontPopupUpdateSelectComponent label='Input Type' value={elementData.name || 'emailAddress'} updateCallback={updateBlockElement} dataKey='name' elementData={elementData} optionData={storefrontPopupFieldNameCollection} />
                <div className='my-4'>
                    <div className='storefront-popup-settings-toggle storefront-popup-settings-main-toggle'>
                        <Toggle
                            id={`toggle_target_device_input_required`}
                            checked={elementData.required || false}
                            title={"Required"}
                            onChange={checked => {
                                const data = getStructuredClone(elementData)
                                data.required = checked;
                                updateBlockElement(data);
                            }}
                        />
                    </div>
                </div>
                <StorefrontPopupUpdateDeviceOnComponent elementData={elementData} updateBlockElement={updateBlockElement}  />
            </div>
            <div>
                <StorefrontPopupElementSettingHeader value={`${storefrontPopupDeviceTypes[currentDevice]} Appearence`} />
                <StorefrontPopupUpdateColorComponent label='Text Color' propertyKey='color' elementData={elementData} deviceStyleKey={deviceStyleKey} updateCallback={handlePopupLayoutStyleChanges} />
                <StorefrontPopupUpdateColorComponent label='Background Color' propertyKey='backgroundColor' elementData={elementData} deviceStyleKey={deviceStyleKey} updateCallback={handlePopupLayoutStyleChanges} />
                <StorefrontPopupUpdateColorComponent label='Border Color' propertyKey='borderColor' elementData={elementData} deviceStyleKey={deviceStyleKey} updateCallback={handlePopupLayoutStyleChanges} />
                <div className='my-4'>
                    <RangeSlider
                        label="Border Width"
                        min={0}
                        max={50}
                        step={1}
                        value={getCleanedPixelValue(getStorefrontPopupBlockStyleValue(elementData, deviceStyleKey, "borderWidth"))}
                        onChange={handlePopupLayoutStyleChanges}
                        propertyName="borderWidth"
                        labelClassName="storefront-popup-block-sub-heading"
                    />
                </div>
                <div className='my-4'>
                    <RangeSlider
                        label="Font Size"
                        min={10}
                        max={50}
                        step={1}
                        value={getCleanedPixelValue(getStorefrontPopupBlockStyleValue(elementData, deviceStyleKey, "fontSize"))}
                        onChange={handlePopupLayoutStyleChanges}
                        propertyName="fontSize"
                        labelClassName="storefront-popup-block-sub-heading"
                    />
                </div>
                <StorefrontPopupUpdateStyleSelectComponent label='Font Style' deviceStyleKey={deviceStyleKey} property='fontStyle' updateCallback={handlePopupLayoutStyleChanges} elementData={elementData} optionData={storefrontPopupFontStyleCollection} />
                <StorefrontPopupFontFamilySelect labelClassName="storefront-popup-block-sub-heading" popupData={popupData} showFontWeightSelect={true} dataObj={elementData} currentDevice={currentDevice} handlePopupLayoutStyleChanges={handlePopupLayoutStyleChanges} parentClassName="my-4" />
                <div className='my-4'>
                    <RangeSlider
                        label="Corner Radius"
                        min={0}
                        max={100}
                        step={1}
                        value={getCleanedPixelValue(getStorefrontPopupBlockStyleValue(elementData, deviceStyleKey, "borderRadius"))}
                        onChange={handlePopupLayoutStyleChanges}
                        propertyName="borderRadius"
                        labelClassName="storefront-popup-block-sub-heading"
                    />
                </div>
                <div className='my-4'>
                    <RangeSlider
                        label="Padding"
                        min={0}
                        max={100}
                        step={1}
                        value={getCleanedPixelValue(getStorefrontPopupBlockStyleValue(elementData, deviceStyleKey, "padding"))}
                        onChange={handlePopupLayoutStyleChanges}
                        propertyName="padding"
                        labelClassName="storefront-popup-block-sub-heading"
                    />
                </div>
                <StorefrontPopupUpdateHeightWidthStyleComponent label='Width' value={widthStyle} defaultPixelValue='200px' propertyKey='width' min={10} max={500} showFullHeight={true} updateCallback={handlePopupLayoutStyleChanges} />
                <StorefrontPopupUpdateHeightWidthStyleComponent label='Height' value={heightStyle} defaultPixelValue='80px' propertyKey='height' min={10} max={500} showFullHeight={false} updateCallback={handlePopupLayoutStyleChanges} />
            </div>
        </>
    )
}

const StorefrontPopupFrequencySelectBlockSettings = (props) => {
    const { elementData, updateBlockElement, currentDevice, popupData } = props
    const deviceStyleKey = `${currentDevice}Styles`
    const handlePopupLayoutStyleChanges = (property, value) => {
        const data = getStructuredClone(elementData)
        if (["fontSize", "borderRadius", "padding", "borderWidth"].includes(property)) {
            data[deviceStyleKey][property] = `${value}px`
        } else {
            data[deviceStyleKey][property] = value
        }
        updateBlockElement(data)
    }
    const widthStyle = getStorefrontPopupBlockStyleValue(elementData, deviceStyleKey, "width")
    const heightStyle = getStorefrontPopupBlockStyleValue(elementData, deviceStyleKey, "height")
    return (
        <>
            <div>
                <StorefrontPopupElementSettingHeader value='Settings' />
                <StorefrontPopupUpdateTextComponent label="Placeholder" updateCallback={updateBlockElement} elementData={elementData} value={elementData.label} />
                <StorefrontPopupUpdateDeviceOnComponent elementData={elementData} updateBlockElement={updateBlockElement}  />
            </div>
            <div>
                <StorefrontPopupElementSettingHeader value={`${storefrontPopupDeviceTypes[currentDevice]} Appearence`} />
                <StorefrontPopupUpdateColorComponent label='Text Color' propertyKey='color' elementData={elementData} deviceStyleKey={deviceStyleKey} updateCallback={handlePopupLayoutStyleChanges} />
                <StorefrontPopupUpdateColorComponent label='Background Color' propertyKey='backgroundColor' elementData={elementData} deviceStyleKey={deviceStyleKey} updateCallback={handlePopupLayoutStyleChanges} />
                <StorefrontPopupUpdateColorComponent label='Border Color' propertyKey='borderColor' elementData={elementData} deviceStyleKey={deviceStyleKey} updateCallback={handlePopupLayoutStyleChanges} />
                <div className='my-4'>
                    <RangeSlider
                        label="Border Width"
                        min={0}
                        max={50}
                        step={1}
                        value={getCleanedPixelValue(getStorefrontPopupBlockStyleValue(elementData, deviceStyleKey, "borderWidth"))}
                        onChange={handlePopupLayoutStyleChanges}
                        propertyName="borderWidth"
                        labelClassName="storefront-popup-block-sub-heading"
                    />
                </div>
                <div className='my-4'>
                    <RangeSlider
                        label="Font Size"
                        min={10}
                        max={50}
                        step={1}
                        value={getCleanedPixelValue(getStorefrontPopupBlockStyleValue(elementData, deviceStyleKey, "fontSize"))}
                        onChange={handlePopupLayoutStyleChanges}
                        propertyName="fontSize"
                        labelClassName="storefront-popup-block-sub-heading"
                    />
                </div>
                <StorefrontPopupUpdateStyleSelectComponent label='Font Style' deviceStyleKey={deviceStyleKey} property='fontStyle' updateCallback={handlePopupLayoutStyleChanges} elementData={elementData} optionData={storefrontPopupFontStyleCollection} />
                <StorefrontPopupFontFamilySelect labelClassName="storefront-popup-block-sub-heading" popupData={popupData} showFontWeightSelect={true} dataObj={elementData} currentDevice={currentDevice} handlePopupLayoutStyleChanges={handlePopupLayoutStyleChanges} parentClassName="my-4" />
                <div className='my-4'>
                    <RangeSlider
                        label="Corner Radius"
                        min={0}
                        max={100}
                        step={1}
                        value={getCleanedPixelValue(getStorefrontPopupBlockStyleValue(elementData, deviceStyleKey, "borderRadius"))}
                        onChange={handlePopupLayoutStyleChanges}
                        propertyName="borderRadius"
                        labelClassName="storefront-popup-block-sub-heading"
                    />
                </div>
                <div className='my-4'>
                    <RangeSlider
                        label="Padding"
                        min={0}
                        max={100}
                        step={1}
                        value={getCleanedPixelValue(getStorefrontPopupBlockStyleValue(elementData, deviceStyleKey, "padding"))}
                        onChange={handlePopupLayoutStyleChanges}
                        propertyName="padding"
                        labelClassName="storefront-popup-block-sub-heading"
                    />
                </div>
                <StorefrontPopupUpdateHeightWidthStyleComponent label='Width' value={widthStyle} defaultPixelValue='200px' propertyKey='width' min={10} max={500} showFullHeight={true} updateCallback={handlePopupLayoutStyleChanges} />
                <StorefrontPopupUpdateHeightWidthStyleComponent label='Height' value={heightStyle} defaultPixelValue='80px' propertyKey='height' min={10} max={500} showFullHeight={false} updateCallback={handlePopupLayoutStyleChanges} />
            </div>
        </>
    )
}

const StorefrontPopupCouponBlockSettings = (props) => {
    const { elementData, updateBlockElement, currentDevice, popupData } = props
    const deviceStyleKey = `${currentDevice}Styles`
    const handlePopupLayoutStyleChanges = (property, value) => {
        const data = getStructuredClone(elementData)
        if (["fontSize", "borderRadius", "padding", "borderWidth"].includes(property)) {
            data[deviceStyleKey][property] = `${value}px`
        } else {
            data[deviceStyleKey][property] = value
        }
        updateBlockElement(data)
    }
    const widthStyle = getStorefrontPopupBlockStyleValue(elementData, deviceStyleKey, "width")
    const heightStyle = getStorefrontPopupBlockStyleValue(elementData, deviceStyleKey, "height")
    return (
        <>
            <div>
                <StorefrontPopupElementSettingHeader value='Settings' />
                <StorefrontPopupUpdateTextComponent label="Coupon Code" updateCallback={updateBlockElement} elementData={elementData} value={elementData.label} />
                <StorefrontPopupUpdateTextComponent label="Click To Copy Message" updateCallback={updateBlockElement} elementData={elementData} value={elementData.clickToCopy} valueKey="clickToCopy" />
                <StorefrontPopupUpdateTextComponent label="Copied Text Message" updateCallback={updateBlockElement} elementData={elementData} value={elementData.copiedText} valueKey="copiedText" />
                <StorefrontPopupUpdateSelectComponent label='Alignment' value={elementData.align} updateCallback={updateBlockElement} dataKey='align' elementData={elementData} optionData={storefrontPopupAlignCollection} />
                <StorefrontPopupUpdateDeviceOnComponent elementData={elementData} updateBlockElement={updateBlockElement}  />
            </div>
            <div>
                <StorefrontPopupElementSettingHeader value={`${storefrontPopupDeviceTypes[currentDevice]} Appearence`} />
                <StorefrontPopupUpdateColorComponent label='Text Color' propertyKey='color' elementData={elementData} deviceStyleKey={deviceStyleKey} updateCallback={handlePopupLayoutStyleChanges} />
                <StorefrontPopupUpdateColorComponent label='Background Color' propertyKey='backgroundColor' elementData={elementData} deviceStyleKey={deviceStyleKey} updateCallback={handlePopupLayoutStyleChanges} />
                <StorefrontPopupUpdateColorComponent label='Border Color' propertyKey='borderColor' elementData={elementData} deviceStyleKey={deviceStyleKey} updateCallback={handlePopupLayoutStyleChanges} />
                <div className='my-4'>
                    <RangeSlider
                        label="Border Width"
                        min={0}
                        max={50}
                        step={1}
                        value={getCleanedPixelValue(getStorefrontPopupBlockStyleValue(elementData, deviceStyleKey, "borderWidth"))}
                        onChange={handlePopupLayoutStyleChanges}
                        propertyName="borderWidth"
                        labelClassName="storefront-popup-block-sub-heading"
                    />
                </div>
                <div className='my-4'>
                    <RangeSlider
                        label="Font Size"
                        min={10}
                        max={50}
                        step={1}
                        value={getCleanedPixelValue(getStorefrontPopupBlockStyleValue(elementData, deviceStyleKey, "fontSize"))}
                        onChange={handlePopupLayoutStyleChanges}
                        propertyName="fontSize"
                        labelClassName="storefront-popup-block-sub-heading"
                    />
                </div>
                <StorefrontPopupUpdateStyleSelectComponent label='Font Style' deviceStyleKey={deviceStyleKey} property='fontStyle' updateCallback={handlePopupLayoutStyleChanges} elementData={elementData} optionData={storefrontPopupFontStyleCollection} />
                <StorefrontPopupFontFamilySelect labelClassName="storefront-popup-block-sub-heading" popupData={popupData} showFontWeightSelect={true} dataObj={elementData} currentDevice={currentDevice} handlePopupLayoutStyleChanges={handlePopupLayoutStyleChanges} parentClassName="my-4" />
                <div className='my-4'>
                    <RangeSlider
                        label="Corner Radius"
                        min={0}
                        max={100}
                        step={1}
                        value={getCleanedPixelValue(getStorefrontPopupBlockStyleValue(elementData, deviceStyleKey, "borderRadius"))}
                        onChange={handlePopupLayoutStyleChanges}
                        propertyName="borderRadius"
                        labelClassName="storefront-popup-block-sub-heading"
                    />
                </div>
                <div className='my-4'>
                    <RangeSlider
                        label="Padding"
                        min={0}
                        max={100}
                        step={1}
                        value={getCleanedPixelValue(getStorefrontPopupBlockStyleValue(elementData, deviceStyleKey, "padding"))}
                        onChange={handlePopupLayoutStyleChanges}
                        propertyName="padding"
                        labelClassName="storefront-popup-block-sub-heading"
                    />
                </div>
                <StorefrontPopupUpdateHeightWidthStyleComponent label='Width' value={widthStyle} defaultPixelValue='200px' propertyKey='width' min={10} max={500} showFullHeight={true} updateCallback={handlePopupLayoutStyleChanges} />
                <StorefrontPopupUpdateHeightWidthStyleComponent label='Height' value={heightStyle} defaultPixelValue='80px' propertyKey='height' min={10} max={500} showFullHeight={false} updateCallback={handlePopupLayoutStyleChanges} />
            </div>
        </>
    )
}


const StorefrontPopupBuilderBlockEdit = (props) => {
    const { show, setShow, editBlockElement, editBlockElementSave, updateBlockElement, addMode, setAddMode, removeElement, currentDevice, popupData } = props
    const [localEditBlockElement, setLocalEditBlockElement] = useState(editBlockElement || {});

    useEffect(() => {
        setLocalEditBlockElement(editBlockElement)
    }, [editBlockElement])

    const updateLocalBlockElementdate = (data) => {
        setLocalEditBlockElement(data)
        updateBlockElement(data)
    }

    const getBlockSettings = () => {
        if (!localEditBlockElement) {
            return null
        }
        switch (editBlockElement.key) {
            case "link":
                return <StorefrontPopupLinkBlockSettings popupData={popupData} currentDevice={currentDevice} elementData={localEditBlockElement} updateBlockElement={updateLocalBlockElementdate} />
            case "image":
                return <StorefrontPopupImageBlockSettings popupData={popupData} currentDevice={currentDevice} elementData={localEditBlockElement} updateBlockElement={updateLocalBlockElementdate} />
            case "button":
                return <StorefrontPopupButtonBlockSettings popupData={popupData} currentDevice={currentDevice} elementData={localEditBlockElement} updateBlockElement={updateLocalBlockElementdate} />
            case "spacing":
                return <StorefrontPopupSpacingBlockSettings popupData={popupData} currentDevice={currentDevice} elementData={localEditBlockElement} updateBlockElement={updateLocalBlockElementdate} />
            case "input":
                return <StorefrontPopupInputBlockSettings popupData={popupData} currentDevice={currentDevice} elementData={localEditBlockElement} updateBlockElement={updateLocalBlockElementdate} />
            case "coupon":
                return <StorefrontPopupCouponBlockSettings popupData={popupData} currentDevice={currentDevice} elementData={localEditBlockElement} updateBlockElement={updateLocalBlockElementdate} />
            case "text":
                return <StorefrontPopupTextBlockSettings popupData={popupData} currentDevice={currentDevice} elementData={localEditBlockElement} updateBlockElement={updateLocalBlockElementdate} />
            case "frequency":
                return <StorefrontPopupFrequencySelectBlockSettings popupData={popupData} currentDevice={currentDevice} elementData={localEditBlockElement} updateBlockElement={updateLocalBlockElementdate} />
            default:
                return null;
        }
    }
    return (
        <>
            {
                show && <div className='storefront-popup-position-relative'>
                    <div
                        className='storefront-popup-builder-block-container'
                    >
                        <div>
                            <div className='d-flex flex-row justify-content-between align-items-end'>
                                {
                                    addMode ?
                                        <div
                                            className='storefront-popup-settings-heading-action'
                                            onClick={() => {
                                                editBlockElementSave(getStructuredClone(localEditBlockElement))
                                            }}
                                        >
                                            Save
                                        </div>
                                        :
                                        <div className='storefront-popup-builder-block-dead-block'></div>
                                }
                                <div className='storefront-popup-settings-heading'>
                                    Edit <span className='storefront-popup-capitalize'>{editBlockElement.key}</span>
                                </div>
                                <div
                                    className='storefront-popup-settings-heading-action'
                                    onClick={() => {
                                        setShow(false)
                                        setAddMode(false)
                                        addMode && removeElement(editBlockElement.id);
                                    }}
                                >
                                    {addMode ? "Cancel" : "Close"}
                                </div>
                            </div>
                            <div className='mt-4 storefront-popup-edit-element-container'>
                                {getBlockSettings()}
                            </div>
                        </div>
                    </div>
                </div>
            }
        </>
    )
}

const StorefrontEditLayoutLayer = (props) => {
    const { popupData, setLocalPopupData, currentDevice } = props
    const deviceStyleKey = `${currentDevice}Styles`

    const handlePopupLayoutStyleChanges = (property, value) => {
        const data = getStructuredClone(popupData)
        if (["height", "width", "padding", "borderRadius"].includes(property)) {
            data[deviceStyleKey][property] = `${value}px`
        } else {
            data[deviceStyleKey][property] = value
        }
        setLocalPopupData(data)
    }

    return (
        <>
            <div className='p-2 px-3'>
                <div className='mb-3 storefront-popup-settings-heading'>
                    Layout
                </div>
                <StorefrontPopupElementSettingHeader value='Settings' />
                <div className='my-4'>
                    <div className='storefront-popup-settings-sub-heading'>
                        Select Position
                    </div>
                    <div>
                        <select
                            className='w-100 mt-2'
                            value={popupData.position || "center"}
                            onChange={
                                (event) => {
                                    const value = event.target.value
                                    const data = getStructuredClone(popupData);
                                    data.position = value;
                                    const defaultPopupData = StorefrontPopupPositionSettings[value]
                                    if (defaultPopupData) {
                                        data.desktopStyles = {
                                            ...data.desktopStyles,
                                            ...defaultPopupData.desktopStyles
                                        }
                                        data.tabletlargeStyles = {
                                            ...data.tabletlargeStyles,
                                            ...defaultPopupData.tabletlargeStyles
                                        }
                                        data.tabletStyles = {
                                            ...data.tabletStyles,
                                            ...defaultPopupData.tabletStyles
                                        }
                                        data.mobilelargeStyles = {
                                            ...data.mobilelargeStyles,
                                            ...defaultPopupData.mobilelargeStyles
                                        }
                                        data.mobileStyles = {
                                            ...data.mobileStyles,
                                            ...defaultPopupData.mobileStyles
                                        }
                                    }
                                    setLocalPopupData(data)
                                }
                            }
                        >
                            <option value="center">Center</option>
                            <option value="left">Left Sidebar</option>
                            <option value="right">Right Sidebar</option>
                            <option value="topBar">Top Bar</option>
                            <option value="bottomBar">Bottom Bar</option>
                            <option value="leftSticky">Left Sticky</option>
                            <option value="rightSticky">Right Sticky</option>
                        </select>
                    </div>
                    <div>
                        {
                            (popupData.layoutType === "2" ? ["1", "2"] : [""]).map((item, index) => {
                                return (
                                    <RangeSlider
                                        key={index}
                                        label={`Column${item ? ` ${item === "1" ? "Left" : "Right"}` : ""} Padding`}
                                        min={0}
                                        max={50}
                                        step={1}
                                        value={getCleanedPixelValue(popupData.layoutPadding[`column${item}`])}
                                        onChange={(property, value) => {
                                            const data = getStructuredClone(popupData)
                                            data.layoutPadding[property] = `${value}px`
                                            setLocalPopupData(data)
                                        }}
                                        propertyName={`column${item}`}
                                        labelClassName="storefront-popup-settings-sub-heading"
                                    />
                                )
                            })
                        }
                    </div>
                    <StorefrontPopupElementSettingHeader value={`${storefrontPopupDeviceTypes[currentDevice]} Appearence`} />
                    {
                        !["topBar", "bottomBar"].includes(popupData.position) && <div>
                            <RangeSlider
                                label="Width"
                                min={getCleanedPixelValue(getStorefrontPopupBlockStyleValue(popupData, deviceStyleKey, "minWidth"))}
                                max={getCleanedPixelValue(getStorefrontPopupBlockStyleValue(popupData, deviceStyleKey, "maxWidth"))}
                                step={1}
                                value={getCleanedPixelValue(getStorefrontPopupBlockStyleValue(popupData, deviceStyleKey, "width"))}
                                onChange={handlePopupLayoutStyleChanges}
                                propertyName="width"
                                labelClassName="storefront-popup-settings-sub-heading"
                                currentDevice={currentDevice}
                            />
                        </div>
                    }
                    {
                        !["left", "right"].includes(popupData.position) && <div>
                            <RangeSlider
                                label="Height"
                                min={getCleanedPixelValue(getStorefrontPopupBlockStyleValue(popupData, deviceStyleKey, "minHeight"))}
                                max={getCleanedPixelValue(getStorefrontPopupBlockStyleValue(popupData, deviceStyleKey, "maxHeight"))}
                                step={1}
                                value={getCleanedPixelValue(getStorefrontPopupBlockStyleValue(popupData, deviceStyleKey, "height"))}
                                onChange={handlePopupLayoutStyleChanges}
                                propertyName="height"
                                labelClassName="storefront-popup-settings-sub-heading"
                                currentDevice={currentDevice}
                            />
                        </div>
                    }
                    <div>
                        <RangeSlider
                            label="Padding"
                            min={0}
                            max={40}
                            step={1}
                            value={getCleanedPixelValue(getStorefrontPopupBlockStyleValue(popupData, deviceStyleKey, "padding"))}
                            onChange={handlePopupLayoutStyleChanges}
                            propertyName="padding"
                            labelClassName="storefront-popup-settings-sub-heading"
                            currentDevice={currentDevice}
                        />
                    </div>
                    <div>
                        <RangeSlider
                            label="Corner Radius"
                            min={0}
                            max={40}
                            step={1}
                            value={getCleanedPixelValue(getStorefrontPopupBlockStyleValue(popupData, deviceStyleKey, "borderRadius"))}
                            onChange={handlePopupLayoutStyleChanges}
                            propertyName="borderRadius"
                            labelClassName="storefront-popup-settings-sub-heading"
                            currentDevice={currentDevice}
                        />
                    </div>
                    <StorefrontPopupFontFamilySelect labelClassName="storefront-popup-settings-sub-heading" popupData={popupData} dataObj={popupData} currentDevice={currentDevice} handlePopupLayoutStyleChanges={handlePopupLayoutStyleChanges} parentClassName="my-4" />
                    <StorefrontPopupUpdateColorComponent label='Font Color' propertyKey='color' labelClassName='storefront-popup-settings-sub-heading' containerClassName='my-0' elementData={popupData} deviceStyleKey={deviceStyleKey} updateCallback={handlePopupLayoutStyleChanges} />
                    <StorefrontPopupUpdateColorComponent label='Background Color' propertyKey='backgroundColor' labelClassName='storefront-popup-settings-sub-heading' containerClassName='my-0' elementData={popupData} deviceStyleKey={deviceStyleKey} updateCallback={handlePopupLayoutStyleChanges} />
                    <div>
                        <div className='storefront-popup-settings-sub-heading'>
                            Background Image
                        </div>
                        <div className='my-3'>
                            <ImageUploader
                                title={false}
                                src={getStorefrontPopupBlockStyleValue(popupData, deviceStyleKey, "backgroundImage")?.match(/^url[(]([\W\w\s\S\d\D]+)[)]$/)?.[1] || ""}
                                closeOnUploaded={false}
                                onUploaded={(image) => {
                                    handlePopupLayoutStyleChanges("backgroundImage", image.original ? `url(${image.original})` : "")
                                }}
                            />
                            <div className='' align="right">
                                <span
                                    className='cursor-pointer text-danger'
                                    onClick={() => {
                                        handlePopupLayoutStyleChanges("backgroundImage", "")
                                    }}
                                >Remove Image</span>
                            </div>
                        </div>
                    </div>
                    <StorefrontPopupElementSettingHeader value='Close Option Settings' />
                    {!["topBar", "bottomBar", "leftSticky", "rightSticky"].includes(popupData.position) && <>
                        <div className='my-4'>
                            <div className='storefront-popup-settings-toggle storefront-popup-settings-main-toggle'>
                                <Toggle
                                    id={`toggle_show_popup_close_button`}
                                    checked={popupData.showCloseButton || false}
                                    title={"Show Close Button"}
                                    onChange={checked => {
                                        const data = getStructuredClone(popupData)
                                        data.showCloseButton = checked;
                                        setLocalPopupData(data);
                                    }}
                                />
                            </div>
                        </div>
                        <div>
                            <div className='storefront-popup-settings-sub-heading'>
                                Backdrop Color
                            </div>
                            <div className='storefront-popup-color-selector'>
                                <ColorPicker
                                    title={""}
                                    color={popupData.backdropColor}
                                    onColorSelected={color => {
                                        const data = getStructuredClone(popupData)
                                        data.backdropColor = color;
                                        setLocalPopupData(data);
                                    }}
                                />
                            </div>
                        </div>
                    </>}
                    <div>
                        <div className='storefront-popup-settings-sub-heading'>
                            Close Button Color
                        </div>
                        <div className='storefront-popup-color-selector'>
                            <ColorPicker
                                title={""}
                                color={getStorefrontPopupBlockStyleValue(popupData, deviceStyleKey, "color")}
                                onColorSelected={color => {
                                    const data = getStructuredClone(popupData)
                                    data.closeButtonColor = color;
                                    setLocalPopupData(data);
                                }}
                            />
                        </div>
                    </div>
                    <div>
                        <div className='storefront-popup-settings-sub-heading'>
                            Close Button Background Color
                        </div>
                        <div className='storefront-popup-color-selector'>
                            <ColorPicker
                                title={""}
                                color={getStorefrontPopupBlockStyleValue(popupData, deviceStyleKey, "backgroundColor")}
                                onColorSelected={color => {
                                    const data = getStructuredClone(popupData)
                                    data.closeButtonBackgroundColor = color;
                                    setLocalPopupData(data);
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

const StorefrontSettingsLayoutLayer = (props) => {
    const { popupData, setLocalPopupData, pages, popupCollection } = props
    const pagesOptions = pages.map((item, index) => {
        return <option key={index} value={item.slugConstant}>{item.pageName}</option>
    })
    const subAndSavePopup = popupCollection.find((o) => o.trigger == subAndSaveTrigger);
    const showSubAndSaveOption = !subAndSavePopup?.id || subAndSavePopup?.id === popupData.id
    return (
        <>
            <div className='p-2'>
                <div className='storefront-popup-settings-heading'>
                    Settings
                </div>
                <div className='p-2'>
                    <StorefrontPopupUpdateTextComponent labelClassName="storefront-popup-settings-sub-heading" label="Popup Name" updateCallback={setLocalPopupData} elementData={popupData} value={popupData.name} valueKey="name" />
                    <div className='my-4'>
                        <div className='storefront-popup-settings-sub-heading'>
                            Select Page
                        </div>
                        <div>
                            <select
                                className='w-100 mt-2'
                                value={popupData.targetPage || "none"}
                                onChange={
                                    (event) => {
                                        const data = getStructuredClone(popupData);
                                        data.targetPage = event.target.value;
                                        setLocalPopupData(data);
                                    }
                                }
                                disabled={popupData.trigger === subAndSaveTrigger}
                            >
                                <option value="none">None</option>
                                <option value="all">All</option>
                                {pagesOptions}
                            </select>
                        </div>
                    </div>
                    <div className='my-4'>
                        <div className='storefront-popup-settings-sub-heading'>
                            Select Trigger
                        </div>
                        <div>
                            <select
                                className='w-100 mt-2'
                                value={popupData.trigger || "none"}
                                onChange={
                                    (event) => {
                                        const data = getStructuredClone(popupData);
                                        if (event.target.value === subAndSaveTrigger) {
                                            data.targetPage = "all";
                                        }
                                        data.trigger = event.target.value;
                                        setLocalPopupData(data);
                                    }
                                }
                            >
                                <option value="none">None</option>
                                <option value="exit">Trigger On Exit</option>
                                <option value="load">Trigger On Load</option>
                                <option value="scrollDown">Trigger On Scroll Down</option>
                                <option value="scrollUp">Trigger On Scroll Up</option>
                                {showSubAndSaveOption && <option value={subAndSaveTrigger}>Subscribe & Save Available</option>}
                            </select>
                        </div>
                    </div>
                    <StorefrontPopupUpdateDeviceOnComponent idPrefix='settings_toggle_target_device_' labelClassName='storefront-popup-settings-sub-heading' elementData={popupData} updateBlockElement={setLocalPopupData} deviceKey='targetDevice' />
                </div>
            </div>
        </>
    )
}

const StorefrontEditPopupLayer = (props) => {
    const { editSection, popupData, editable, setCurrentPopupHandler, pages, onSaveClicked, previewMode, selectedDevice, campaignId, popupCollection } = props;
    const [localPopupData, setLocalPopupData] = useState(getStructuredClone(popupData))
    const [addNewBlock, setAddNewBlock] = useState(false)
    const [editBlockElement, setEditBlockElement] = useState(null)
    const [showEditBlockElement, setShowEditBlockElement] = useState(false)
    const [addMode, setAddMode] = useState(false)
    const [version, setVersion] = useState(1)

    useEffect(() => {
        if (onSaveClicked) {
            setCurrentPopupHandler(localPopupData);
        }
    }, [onSaveClicked])

    const resetCurrentPopupData = () => {
        setLocalPopupData(getStructuredClone(popupData))
    }

    const handlePopupLayoutUpdate = (layout, layoutType) => {
        const data = getStructuredClone(localPopupData)
        data.layout = layout
        data.layoutType = layoutType
        setLocalPopupData(data)
        setVersion((v) => v + 1)
    }

    const updateBlockElement = (element) => {
        if (element) {
            const data = getStructuredClone(localPopupData)
            const index = data.layout.findIndex((item) => item.id === element.id)
            if (index > -1) {
                data.layout[index] = element
            }
            setLocalPopupData(getStructuredClone(data))
            setVersion((v) => v + 1)
        }
    }

    const editBlockElementSave = (element) => {
        updateBlockElement(element)
        setEditBlockElement(null)
        setShowEditBlockElement(false)
        setAddMode(false)
    }

    const setNewElement = (element) => {
        if (element) {
            setEditBlockElement(getStructuredClone(element))
            setShowEditBlockElement(true)
            setAddNewBlock(false)
        }
    }

    const removeElement = (elementId) => {
        if (elementId) {
            const index = localPopupData?.layout?.findIndex((item) => item.id === elementId)
            if (index > -1) {
                const data = getStructuredClone(localPopupData)
                data.layout = data.layout.filter((item) => item.id !== elementId)
                setEditBlockElement(null)
                setLocalPopupData(data)
                setVersion((v) => v + 1)
            }
        }
    }

    const setNewElementFromBlocks = (element) => {
        setAddMode(true)
        if (element) {
            setEditBlockElement(getStructuredClone(element))
            setShowEditBlockElement(true)
            setAddNewBlock(false)
            const data = getStructuredClone(localPopupData)
            if (!data.layout) {
                data.layout = [element]
            } else {
                data.layout.push(element)
            }
            setLocalPopupData(getStructuredClone(data))
            setVersion((v) => v + 1)
        }
    }

    const getEditLayer = () => {
        switch (editSection) {
            case "layout":
                return <StorefrontEditLayoutLayer currentDevice={selectedDevice} setLocalPopupData={setLocalPopupData} popupData={localPopupData} />
            case "settings":
                return <StorefrontSettingsLayoutLayer popupCollection={popupCollection} pages={pages} setLocalPopupData={setLocalPopupData} popupData={localPopupData} />
            case "builder":
                return <StorefrontEditBuilderLayer resetCurrentPopupData={resetCurrentPopupData} removeElement={removeElement} popupData={localPopupData} defaultPopupData={popupData} setEditElement={setNewElement} handlePopupLayoutUpdate={handlePopupLayoutUpdate} setAddNewBlock={setAddNewBlock} />
            default:
                return <StorefrontEditBuilderLayer resetCurrentPopupData={resetCurrentPopupData} removeElement={removeElement} popupData={localPopupData} defaultPopupData={popupData} setEditElement={setNewElement} handlePopupLayoutUpdate={handlePopupLayoutUpdate} setAddNewBlock={setAddNewBlock} />
        }
    };
    return (
        <>
            <StorefrontPopupBuilderBlocks subAndSavePopup={popupData?.trigger === subAndSaveTrigger} show={addNewBlock} setShow={setAddNewBlock} setNewElement={setNewElementFromBlocks} layoutType={localPopupData.layoutType} />
            <StorefrontPopupBuilderBlockEdit popupData={localPopupData} currentDevice={selectedDevice} removeElement={removeElement} setAddMode={setAddMode} addMode={addMode} show={showEditBlockElement} setShow={setShowEditBlockElement} editBlockElement={editBlockElement} editBlockElementSave={editBlockElementSave} layoutType={localPopupData.layoutType} updateBlockElement={updateBlockElement} />
            {getEditLayer(editSection)}
            <StorefrontPopupContainer campaignId={campaignId} editMode={true} previewMode={previewMode} key={version} editable={editable} popupData={localPopupData} savedPopupData={popupData} currentDevice={selectedDevice} />
        </>
    )
}

const PopupManagerModal = (props) => {
    const { editable, onHide, websitePopupCollection, website } = props
    const [popupCollection, setPopupCollection] = useState(websitePopupCollection)
    const [editSection, setEditSection] = useState("builder")
    const [showExitModal, setShowEditModal] = useState(false)
    const [popupEditMode, setPopupEditMode] = useState(false)
    const [popupAddMode, setPopupAddMode] = useState(false)
    const [currentPopup, setCurrentPopup] = useState(null)
    const [pages, setPages] = useState([])
    const [onSaveClicked, setOnSaveClicked] = useState(false)
    const [deletePopupId, setDeletePopupId] = useState(null)
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [showBackDropScreen, setShowBackDropScreen] = useState(false)
    const [previewMode, setPreviewMode] = useState(website?.extra?.popup_preview_mode || false)
    const [selectedDevice, setSelectedDevice] = useState("desktop")
    const [deviceIconClicked, setDeviceIconClicked] = useState(false)

    useEffect(() => {
        const getPages = (list) => {
            return list?.filter((item) => !item.is_homepage)?.map((item) => {
                return {
                    slugConstant: item.slug,
                    slugValue: item.slug,
                    pageName: item.title 
                }
            })
        }
        setPages(storefrontPopupPages(website))
        if (!window.PAGES?.length) {
            Backend.getPages().then((data) => {
                setPages([...storefrontPopupPages(website), ...getPages(data)])
            }).catch(() => {})
        } else {
            setPages([...storefrontPopupPages(website), ...getPages(window.PAGES)])
        }
    }, [website])

    const popupDeleteHandler = (id) => {
        if (id) {
            const index = popupCollection.findIndex((item) => item.id === id)
            if (index > -1) {
                setShowBackDropScreen(true)
                const data = popupCollection.filter((item) => item.id !== id)
                website.extra.popup_collection = data
                try {
                    Backend.updateWebsite({
                        slug: website.slug,
                        extra: website.extra
                    })
                    setPopupCollection(data)
                    setShowBackDropScreen(false)
                    General.showNotification("Popup Deleted")
                } catch (err) {
                    setShowBackDropScreen(false)
                    General.showNotification(err?.message, false)
                    website.extra.popup_collection = popupCollection || []
                }
            }
        }
    }

    const setPopupPreviewMode = (value) => {
        if (!website.extra) {
            website.extra = {}
        }
        website.extra.popup_preview_mode = value
        try {
            Backend.updateWebsite({
                slug: website.slug,
                extra: website.extra
            })
            setPreviewMode(value)
            General.showNotification("Saved")
        } catch (err) {
            website.extra.popup_preview_mode = !value
            setPreviewMode(!value)
            General.showNotification(err?.message, false)
        }
    }

    const setCurrentPopupHandler = (data) => {
        if (onSaveClicked && data) {
            const index = popupCollection.findIndex((item) => item.id === data.id)
            const collection = [...popupCollection]
            if (!data.name) {
                data.name = "Popup " + data.id
            }
            if (index > -1) {
                collection[index] = data
            } else {
                collection.push(data)
            }
            if (!website.extra) {
                website.extra = {}
            }
            website.extra.popup_collection = collection || []
            try {
                Backend.updateWebsite({
                    slug: website.slug,
                    extra: website.extra
                })
                setCurrentPopup(getStructuredClone(data))
                setPopupCollection(collection)
                General.showNotification("Saved")
                setOnSaveClicked(false)
            } catch (err) {
                General.showNotification(err?.message, false)
                website.extra.popup_collection = popupCollection || []
                setOnSaveClicked(false)
            }
        }
    }

    return (
        <>
            {
                popupEditMode && <div className='storefront-popup-manager-left-settings'>
                    <div>
                        {deviceIconClicked && <div className='storefront-popup-position-relative'>
                            <div className='storefront-settings-device-icons-container'>
                                {
                                    storefrontPopupDeviceTypesCollection.map((item) => {
                                        return <div
                                            className={item.device === selectedDevice ? "storefront-settings-selected-device-icons" : ""}
                                            onClick={() => {
                                                setSelectedDevice(item.device)
                                                setDeviceIconClicked(false)
                                            }}
                                            dangerouslySetInnerHTML={{ __html: item.svg }}
                                        ></div>
                                    })
                                }
                            </div>
                        </div>}
                        <div
                            className='storefront-settings-icon-container'
                            onClick={() => {
                                setDeviceIconClicked(!deviceIconClicked)
                            }}
                        >
                            <div>
                                {
                                    storefrontPopupDeviceTypesCollection.filter((item) => item.device === selectedDevice).map((item, index) => {
                                        return <div
                                            key={index}
                                            dangerouslySetInnerHTML={{ __html: item.svg }}
                                        ></div>
                                    })
                                }
                            </div>
                            <div className='storefront-settings-text'>Device</div>
                        </div>
                        <div
                            className={`storefront-settings-icon-container ${editSection === "builder" ? 'storefront-settings-icon-container-selected' : ''}`}
                            onClick={() => {
                                setEditSection("builder")
                            }}
                        >
                            <i className='fa-pen fas storefront-settings-icon'></i>
                            <div className='storefront-settings-text'>Builder</div>
                        </div>
                        <div
                            className={`storefront-settings-icon-container ${editSection === "layout" ? 'storefront-settings-icon-container-selected' : ''}`}
                            onClick={() => {
                                setEditSection("layout")
                            }}
                        >
                            <i className='fa-th-large fas storefront-settings-icon'></i>
                            <div className='storefront-settings-text'>Layout</div>
                        </div>
                        <div
                            className={`storefront-settings-icon-container ${editSection === "settings" ? 'storefront-settings-icon-container-selected' : ''}`}
                            onClick={() => {
                                setEditSection("settings")
                            }}
                        >
                            <i className='fa-cog fas storefront-settings-icon'></i>
                            <div className='storefront-settings-text'>Settings</div>
                        </div>
                        <div
                            className='storefront-settings-icon-container'
                            onClick={() => {
                                setPopupEditMode(false)
                                setCurrentPopup(null)
                            }}
                        >
                            <i className='fa-arrow-left fas storefront-settings-icon'></i>
                            <div className='storefront-settings-text'>Back</div>
                        </div>
                        <div
                            className='storefront-settings-icon-container'
                            onClick={() => {
                                setOnSaveClicked(true)
                            }}
                        >
                            <i className='fa-save fas storefront-settings-icon'></i>
                            <div className='storefront-settings-text'>Save</div>
                        </div>
                        <div
                            className='storefront-settings-icon-container'
                            onClick={() => {
                                setShowEditModal(true)
                            }}
                        >
                            <i className='fa-sign-out-alt fas storefront-settings-icon'></i>
                            <div className='storefront-settings-text'>Exit</div>
                        </div>
                    </div>
                </div>
            }
            <div
                className='boxed boxed--lg m-t-30 sm-settings storefront-popup-manager-right-settings'
                style={
                    {
                        left: popupEditMode ? "60px" : 0,
                        width: popupEditMode ? "340px" : "400px"
                    }
                }
            >
                {!popupEditMode && <EditCreateStorefrontPopups previewMode={previewMode} setPopupPreviewMode={setPopupPreviewMode} pages={pages} setShowDeleteModal={setShowDeleteModal} setDeletePopupId={setDeletePopupId} setPopupEditMode={setPopupEditMode} setPopupAddMode={setPopupAddMode} onHide={onHide} setCurrentPopup={setCurrentPopup} popupCollection={popupCollection} />}
                {(popupEditMode || popupAddMode) && currentPopup && <StorefrontEditPopupLayer popupCollection={popupCollection} campaignId={website.campaign_id} selectedDevice={selectedDevice} previewMode={previewMode} pages={pages} editSection={editSection} popupData={currentPopup} editable={editable} onSaveClicked={onSaveClicked} setCurrentPopupHandler={setCurrentPopupHandler} />}
            </div>
            <style>
                {
                    `
                    .modal-container {
                        z-index: 1020;
                    }
                    `
                }
            </style>
            <DeleteModal
                showModal={showExitModal}
                onDelete={() => {
                    setPopupEditMode(false)
                    setCurrentPopup(null)
                    onHide()
                }}
                onHide={() => {
                    setShowEditModal(false)
                }}
                confirmLabel="Confirm"
                modalHeader={"Exit"}
                htmlContent={<h4>
                    Are you sure you want to exit?
                </h4>}
            />
            <DeleteModal
                showModal={showDeleteModal}
                onDelete={() => {
                    popupDeleteHandler(deletePopupId)
                    setShowDeleteModal(false)
                    setDeletePopupId(null)
                }}
                onHide={() => {
                    setShowDeleteModal(false)
                    setDeletePopupId(null)
                }}
                confirmLabel="Delete"
                modalHeader={"Delete"}
                htmlContent={<h4>
                    Are you sure you want to delete popup?
                </h4>}
            />
            {(onSaveClicked || showBackDropScreen) && <div className='storefront-popup-onsave-backdrop'></div>}
        </>
    )
}

export const StorefrontPopupWrapper = ({ website, editableProp, updateCart }) => {
    const location = window.location;
    const [activePopup, setActivePopup] = useState(null)
    const [subAndSaveData, setSubAndSaveData] = useState(null)
    const popupQueue = useRef([]);
    const hasShown = useRef(JSON.parse(sessionStorage.getItem("ccStorefrontPopHasShown")) || {});
    const isPopupOpen = useRef(false);
    const lastScrollY = useRef(window.scrollY);
    const routeKey = location.pathname.replace(/^\//, '') || '';
    const [deviceType, setDeviceType] = useState(getDeviceType(window.innerWidth));
    const editable = editableProp !== undefined ? editableProp : (website?.editable || false)

    useEffect(() => {
        const handleResize = () => {
            const currentDevice = getDeviceType(window.innerWidth);
            setDeviceType(prevDevice => (prevDevice !== currentDevice ? currentDevice : prevDevice));
        };
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const enqueuePopup = (popup) => {
        const exists = popupQueue.current.some(
            (p) => p.id === popup.id
        );
        if (!exists || popup.trigger === "click") {
            popupQueue.current.push(popup);
            showNextPopup();
        }
    };

    const showNextPopup = () => {
        if (isPopupOpen.current || popupQueue.current.length === 0) return;
        const nextPopup = popupQueue.current.shift()
        setActivePopup(nextPopup)
        isPopupOpen.current = true
    };

    const handleClosePopup = () => {
        if (activePopup?.trigger === subAndSaveTrigger) {
            setSubAndSaveData(null);
        }
        setActivePopup(null);
        isPopupOpen.current = false;
        setTimeout(showNextPopup, 2000);
    };

    const getPageSlug = () => {
        let pageSlug = routeKey?.trim()
        if (!pageSlug || pageSlug === "/") {
            pageSlug = website["homepage_slug"]
        } else if ((new RegExp(`^${website.collection_page_slug}/\\d+$`, "ig")).test(pageSlug)) {
            pageSlug = website.collection_page_slug
        } else if ((new RegExp(`^${website.product_page_slug}/\\d+$`, "ig")).test(pageSlug)) {
            pageSlug = website.product_page_slug
        }
        return pageSlug
    }

    const matchTrigger = (triggerType, popupId) => {
        let pageSlug = getPageSlug()
        return website?.extra?.popup_collection?.filter((rule) => {
            return (rule.targetPage === "all" || (website[rule.targetPage] ? website[rule.targetPage] === pageSlug : pageSlug === rule.targetPage)) &&
                (popupId ? true : rule.trigger === triggerType) &&
                rule.targetDevice[deviceType] === true &&
                (popupId ? true : !hasShown.current[`${rule.id}${pageSlug}`])
        }) || []
    };

    const handleTrigger = (triggerType, popupId) => {
        const matched = matchTrigger(triggerType, popupId);
        matched.forEach(rule => {
            if (popupId) {
                if (popupId === `${rule.id}`) {
                    enqueuePopup(rule)
                } 
            } else {
                let pageSlug = getPageSlug()
                hasShown.current[`${rule.id}${pageSlug}`] = true;
                sessionStorage.setItem("ccStorefrontPopHasShown", JSON.stringify(hasShown.current || {}))
                enqueuePopup(rule)
            }
        });
    };

    const handleScroll = () => {
        const currentY = window.scrollY;
        if (currentY > lastScrollY.current) {
            handleTrigger("scrollDown");
        } else if (currentY < lastScrollY.current) {
            handleTrigger("scrollUp");
        }
        lastScrollY.current = currentY;
    };

    const handleExitIntent = (e) => {
        if (e.clientY <= 0) {
            handleTrigger("exit");
        }
    };

    const handleButtonClick = (e) => {
        const popupId = e?.detail || e?.ccPopupId
        if (popupId) {
            handleTrigger("click", popupId)
        }
    };

    const handleSubAndSaveTrigger = (e) => {
        const popup = website?.extra?.popup_collection?.find((o) => o.trigger === subAndSaveTrigger)
        if (popup) {
            setSubAndSaveData(e?.detail)
            handleTrigger("click", popup.id?.toString())
        }
    }

    const attachEventListeners = () => {
        setTimeout(() => handleTrigger("load"), 300);
        window.addEventListener("scroll", handleScroll);
        document.addEventListener("mouseout", handleExitIntent);
        window.addEventListener('cc-popup-click-event', handleButtonClick);
        window.addEventListener(subAndSaveEventName, handleSubAndSaveTrigger);
    }

    const removeEventListeners = () => {
        window.removeEventListener("scroll", handleScroll);
        document.removeEventListener("mouseout", handleExitIntent);
        window.removeEventListener('cc-popup-click-event', handleButtonClick);
        window.addEventListener(subAndSaveEventName, handleSubAndSaveTrigger);
    }

    useEffect(() => {
        hasShown.current = JSON.parse(sessionStorage.getItem("ccStorefrontPopHasShown")) || {};
        popupQueue.current = [];
        setActivePopup(null);
        isPopupOpen.current = false;

        attachEventListeners();
        return () => removeEventListeners();
    }, [routeKey]);

    return (
        <>
            {(editable ? website?.extra?.popup_preview_mode : true) && activePopup && (
                <StorefrontPopupContainer
                    previewMode={true}
                    editable={editable}
                    savedPopupData={activePopup}
                    popupData={activePopup}
                    currentDevice={deviceType}
                    handleClosePopup={handleClosePopup}
                    editMode={false}
                    campaignId={website.campaign_id}
                    setSubAndSaveData={setSubAndSaveData}
                    subAndSaveData={subAndSaveData}
                    updateCart={updateCart}
                />
            )}
        </>
    );
};

export default PopupManagerModal